import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import get from 'lodash/get';
import { TableRow, TableCell } from '@mui/material';
import PhoneIcon from '@mui/icons-material/Phone';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import { compact } from 'lodash';
import { useTranslation } from '../../src/i18n';
import SimpleTable from '../../components/FbTable/SimpleTable';
import { useFBTableStyles } from '../../components/FbTable';
import FbTableCard, { useTableCardStyles } from '../../components/FbTable/FbTableCard';
import { colors } from '../../constants/styleConstants';
import { TP } from 'constants/index';
import usePrivacyAttributes from '../../utils/privacy';

const TeamData = React.memo(function TeamData({ contacts }) {
  return contacts.map(person => {
    const phone = get(person, 'agentPhoneNumber');
    const email = get(person, 'agentEmail');
    return (
      <div key={`contactPerson-${get(person, 'id')}`} style={{ marginBottom: 10 }}>
        <div>{get(person, 'agentName', '')}</div>
        {phone && (
          <a
            href={`tel:${get(person, 'agentPhoneNumber')}`}
            title="agentPhoneNumber"
            style={{ display: 'flex', alignItems: 'center' }}
          >
            <PhoneIcon style={{ fill: colors.primary, fontSize: '1rem' }} />
            {get(person, 'agentPhoneNumber', '')}
          </a>
        )}
        {email && (
          <a
            href={`mailto:${get(person, 'agentEmail')}`}
            title="agentEmail"
            style={{ display: 'flex', alignItems: 'center' }}
          >
            <MailOutlineIcon style={{ fill: colors.primary, fontSize: '1rem' }} />
            {get(person, 'agentEmail', '')}
          </a>
        )}
      </div>
    );
  });
});

const getCountryAndCityInfo = data => compact([get(data, 'country.name', ''), get(data, 'city.name', '')]).join(', ');

const OfficeContacts = React.memo(function OfficeContacts({ data }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column' }}>
      <a href={`mailto:${get(data, 'contacts.email')}`} title="Email">
        {get(data, 'contacts.email', '')}
      </a>
      <a href={`tel:${get(data, 'contacts.phone')}`}>{get(data, 'contacts.phone')}</a>
      <a href={`tel:${get(data, 'contacts.mobilePhone')}`}>{get(data, 'contacts.mobilePhone')}</a>
    </div>
  );
});

const OfficesRow = ({ data }) => {
  const { classes, cx } = useFBTableStyles();
  return (
    <TableRow>
      <TableCell className={cx(classes.cell, classes.bold)}>{get(data, 'country.name', '')}</TableCell>
      <TableCell className={classes.cell} style={{ whiteSpace: 'pre-wrap' }}>
        {get(data, 'address', '')}
      </TableCell>
      <TableCell className={classes.cell}>
        <OfficeContacts data={data} />
      </TableCell>
      <TableCell className={classes.cell}>
        <TeamData contacts={get(data, 'contactPersons', [])} />
      </TableCell>
    </TableRow>
  );
};

OfficesRow.propTypes = {
  data: PropTypes.object,
};

const OfficesCard = ({ data }) => {
  const { classes } = useTableCardStyles();
  const { t } = useTranslation();

  return (
    <FbTableCard>
      <div className={classes.infoElement}>
        <span className={classes.infoTitle}>{t(`${TP}.m_COUNTRY`)}</span>
        <div>{getCountryAndCityInfo(data)}</div>
      </div>
      <div className={classes.infoElement} style={{ whiteSpace: 'pre-wrap' }}>
        <span className={classes.infoTitle}>{t(`${TP}.m_OFFICEADDRESS`)}</span>
        {get(data, 'address', '')}
      </div>
      <div className={classes.infoElement}>
        <span className={classes.infoTitle}>{t(`${TP}.m_CONTACTDETAILS`)}</span>
        <OfficeContacts data={data} />
      </div>
      <div className={classes.infoElement}>
        <span className={classes.infoTitle}>{t(`${TP}.m_TEAM`)}</span>
        <TeamData contacts={get(data, 'contactPersons', [])} />
      </div>
    </FbTableCard>
  );
};

OfficesCard.propTypes = {
  data: PropTypes.object,
};

const AgencyOfficesTable = ({ offices }) => {
  const { t } = useTranslation();
  const { canSee } = usePrivacyAttributes();

  const cellsNames = [`${TP}.m_COUNTRY`, `${TP}.m_OFFICEADDRESS`, `${TP}.m_CONTACTDETAILS`, `${TP}.m_TEAM`].map(name =>
    t(name),
  );

  const officesWithFilteredContacts = useMemo(
    () =>
      offices?.map(office => ({
        ...office,
        contactPersons: office?.contactPersons?.filter(cp => canSee(cp?.privacyAttributes)),
      })),
    [canSee, offices],
  );

  return (
    <SimpleTable
      headers={cellsNames}
      customElements={{
        row: <OfficesRow />,
        card: <OfficesCard />,
      }}
      data={officesWithFilteredContacts}
    />
  );
};

AgencyOfficesTable.propTypes = {
  offices: PropTypes.array,
};

export default AgencyOfficesTable;
